import _regenerator from "@babel/runtime/helpers/esm/regenerator";
import _asyncToGenerator from "@babel/runtime/helpers/esm/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { useCallback, useState } from 'react';
import { Button, CloseButton } from '@instructure/ui-buttons';
import { View } from '@instructure/ui-view';
import { Flex } from '@instructure/ui-flex';
import { Text } from '@instructure/ui-text';
import { Tray } from '@instructure/ui-tray';
import t from 'format-message';
import { colors } from '@instructure/canvas-theme';
import { useGradebookApp } from "../../context/GradebookAppContext/index.js";
import { showFlashAlert } from "../../../FlashAlert/index.js";
/**
 * SettingsTray component that provides the tray chrome and renders the SettingsTrayContent.
 * This component handles:
 * - Tray header, footer, layout
 * - Form state management (local copy of settings)
 * - Save/cancel flow with async persistence
 * - Loading states and error handling
 * The SettingsTrayContent is purely presentational and renders the form fields.
 */
export var SettingsTray = function SettingsTray(_ref) {
  var open = _ref.open,
    onDismiss = _ref.onDismiss,
    SettingsTrayContent = _ref.SettingsTrayContent;
  var _useGradebookApp = useGradebookApp(),
    _useGradebookApp$sett = _useGradebookApp.settings,
    contextSettings = _useGradebookApp$sett.settings,
    setSettings = _useGradebookApp$sett.setSettings,
    onSave = _useGradebookApp$sett.onSave;
  var _useState = useState(contextSettings),
    _useState2 = _slicedToArray(_useState, 2),
    localSettings = _useState2[0],
    setLocalSettings = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSaving = _useState4[0],
    setIsSaving = _useState4[1];

  // Update local settings when context settings change or tray opens
  React.useEffect(function () {
    if (open) {
      setLocalSettings(contextSettings);
    }
  }, [open, contextSettings]);
  var resetForm = useCallback(function () {
    setLocalSettings(contextSettings);
  }, [contextSettings]);
  var saveSettings = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      var result, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            setIsSaving(true);
            _context.p = 1;
            _context.n = 2;
            return onSave(localSettings);
          case 2:
            result = _context.v;
            if (result.success) {
              setSettings(localSettings);
              showFlashAlert({
                message: t('Your settings have been saved.'),
                type: 'success'
              });
              onDismiss();
            } else {
              showFlashAlert({
                message: result.error || t('There was an error saving your settings. Please try again.'),
                type: 'error'
              });
            }
            _context.n = 4;
            break;
          case 3:
            _context.p = 3;
            _t = _context.v;
            showFlashAlert({
              message: t('An unexpected error occurred while saving settings'),
              type: 'error',
              err: _t
            });
          case 4:
            _context.p = 4;
            setIsSaving(false);
            return _context.f(4);
          case 5:
            return _context.a(2);
        }
      }, _callee, null, [[1, 3, 4, 5]]);
    }));
    return function saveSettings() {
      return _ref2.apply(this, arguments);
    };
  }();
  return /*#__PURE__*/React.createElement(Tray, {
    label: t('Settings Tray'),
    placement: "end",
    size: "small",
    open: open,
    onDismiss: onDismiss,
    "data-testid": "lmgb-settings-tray"
  }, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    height: "100vh",
    justifyItems: "space-between"
  }, /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    padding: "medium"
  }, /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    justifyItems: "space-between",
    "data-testid": "lmgb-settings-header"
  }, /*#__PURE__*/React.createElement(Text, {
    size: "x-large",
    weight: "bold"
  }, t('Settings')), /*#__PURE__*/React.createElement(CloseButton, {
    size: "medium",
    screenReaderLabel: t('Close Settings Tray'),
    onClick: onDismiss,
    "data-testid": "lmgb-close-settings-button"
  })), /*#__PURE__*/React.createElement("hr", {
    style: {
      marginBottom: '0',
      marginTop: '16px'
    }
  })), /*#__PURE__*/React.createElement(Flex, {
    direction: "column",
    padding: "small medium",
    alignItems: "stretch",
    gap: "medium"
  }, /*#__PURE__*/React.createElement(SettingsTrayContent, {
    settings: localSettings,
    onChange: setLocalSettings
  }))), /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(View, {
    as: "div",
    background: "secondary",
    borderColor: colors.primitives.grey14,
    borderWidth: "small none none none",
    padding: "small",
    margin: "large none none none"
  }, /*#__PURE__*/React.createElement(Flex, {
    gap: "x-small",
    justifyItems: "end"
  }, /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Button, {
    onClick: function onClick() {
      resetForm();
      onDismiss();
    },
    color: "secondary",
    disabled: isSaving,
    "data-testid": "lmgb-cancel-settings-button"
  }, t('Cancel'))), /*#__PURE__*/React.createElement(Flex.Item, null, /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    onClick: saveSettings,
    disabled: isSaving,
    "data-testid": "lmgb-save-settings-button"
  }, isSaving ? t('Saving') : t('Save'))))))));
};